import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import type { RootState } from 'store';
import type { CustomerForForms } from 'types/customer';
import { CONDITIONS } from 'types/notification';

/**
 * 顧客一覧の EintityAdapter
 */
const customersAdapter = createEntityAdapter<CustomerForForms>({
  selectId: (customer) => customer.id,
  sortComparer: (a, b) => a.id - b.id,
});

/**
 * 顧客一覧の EintityState に外挿する状態
 */
type ExtraState = {
  /**
   * 顧客一覧のロード中フラグ
   */
  loading: boolean;
  /**
   * 顧客 ID の検索フィールド
   * NOTE: 顧客 ID は number だが検索フォームは string なのでここは string 型にしておく
   */
  id: string;
  /**
   * 顧客名の検索フィールド
   */
  name: string;
  /**
   * 顧客詳細の検索フィールド
   */
  detail: string;
};

/**
 * 顧客一覧の EintityState に外挿する状態の初期値
 */
const initExtraState: ExtraState = {
  loading: false,
  id: '',
  name: '',
  detail: '',
};

/**
 * 顧客一覧の slice(reducer + action)
 */
export const customersSlice = createSlice({
  name: 'customers',
  initialState: customersAdapter.getInitialState(initExtraState),
  reducers: {
    init: () => customersAdapter.getInitialState(initExtraState),
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    idChanged: (
      state,
      action: PayloadAction<{
        id: string;
      }>,
    ) => {
      state.id = action.payload.id;
    },
    nameChanged: (
      state,
      action: PayloadAction<{
        name: string;
      }>,
    ) => {
      state.name = action.payload.name;
    },
    detailChanged: (
      state,
      action: PayloadAction<{
        detail: string;
      }>,
    ) => {
      state.detail = action.payload.detail;
    },
    fetchCustomersSuccess: customersAdapter.setAll,
    customerUpdated: customersAdapter.updateOne,
    customersUpdated: customersAdapter.updateMany,
    /**
     * 顧客情報の選択状態をクリアします。
     */
    uncheckAllCustomers: (state) => {
      customersAdapter.updateMany(
        state,
        state.ids.map((id) => ({
          id,
          changes: {
            isSelected: false,
          },
        })),
      );
    },
  },
});

/**
 * 顧客一覧の action
 */
export const customersActions = customersSlice.actions;

/**
 * 顧客一覧の selectors（内部用）
 */
const { selectAll, selectTotal } = customersAdapter.getSelectors<RootState>(
  (state) => state.releaseTask.customers,
);
const getLoading = (state: RootState) => state.releaseTask.customers.loading;
const getId = (state: RootState) => state.releaseTask.customers.id;
const getName = (state: RootState) => state.releaseTask.customers.name;
const getDetail = (state: RootState) => state.releaseTask.customers.detail;

/**
 * 別ステート（`registrationSlice`）から参照する値。
 *
 * 残念なことに、一部、`registrationSlice`にある値を参照しなければならない。
 */
const registrationState = {
  /**
   * `registrationSlice`の`condition`を返します。
   */
  getCondition: (state: RootState) => state.releaseTask.registration.condition,
  /**
   * `registrationSlice`の`targetGroup`を返します。
   */
  getTargetGroup: (state: RootState) =>
    state.releaseTask.registration.targetGroup,
};

// 選択している情報ででフィルタリングされた顧客一覧
const getFilteredCustomers = createSelector(
  selectAll,
  getId,
  getName,
  getDetail,
  registrationState.getCondition,
  registrationState.getTargetGroup,
  (customers, id, name, detail, condition, targetGroup) =>
    customers
      .filter((customer) => customer.id.toString().indexOf(id) !== -1)
      .filter((customer) => customer.name.indexOf(name) !== -1)
      .filter((customer) => customer.detail.indexOf(detail) !== -1)
      .filter((customer) => {
        // `顧客配信対象`と`ターゲットグループ`で抽出を行いたい

        if (condition === CONDITIONS.ALL) {
          // `全顧客`を選んでいる場合はこれらの値関係なく、すべての顧客を対象としたい
          return true;
        }

        // `全顧客`以外は選択している`ターゲットグループ`の顧客は対象としたい
        return customer.system_update_group.indexOf(targetGroup) !== -1;
      }),
);
// 選択された顧客一覧
const getSelectedCustomers = createSelector(selectAll, (customers) =>
  customers.filter((customer) => customer.isSelected),
);
// 選択された顧客一覧の要素数
const getSelectedCustomersLength = createSelector(
  selectAll,
  (customers) => customers.filter((customer) => customer.isSelected).length,
);
// チェックボックスが全てチェックされていたら true
const getIsSelectedAll = createSelector(getFilteredCustomers, (customers) =>
  customers.every((customer) => customer.isSelected),
);
// チェックボックスが１つ以上全て未満チェックされていたら true
const getIsIndeterminate = createSelector(
  getFilteredCustomers,
  getIsSelectedAll,
  (customers, isSelectedAll) =>
    customers.some((customer) => customer.isSelected) && !isSelectedAll,
);

/**
 * 顧客一覧の selectors
 */
export const customersSelectors = {
  loading: getLoading,
  id: getId,
  name: getName,
  detail: getDetail,
  total: selectTotal,
  filteredCustomers: getFilteredCustomers,
  selectedCustomers: getSelectedCustomers,
  selectedCustomersLength: getSelectedCustomersLength,
  isSelectedAll: getIsSelectedAll,
  isIndeterminate: getIsIndeterminate,
} as const;
